import { useState } from "react";
import { Link } from "react-router-dom";
import { useWorksSearch } from "@/api/hooks";
import type { WorkSummary } from "@/api/types";

// Work form labels in French
const WORK_FORM_LABELS: Record<string, string> = {
  novel: "Roman",
  short_story: "Nouvelle",
  poetry: "Poésie",
  theater: "Théâtre",
  essay: "Essai",
  biography: "Biographie",
  documentary: "Documentaire",
  film: "Film",
  series: "Série",
  animation: "Animation",
  symphony: "Symphonie",
  opera: "Opéra",
  concerto: "Concerto",
  song: "Chanson",
  album: "Album",
  painting: "Peinture",
  sculpture: "Sculpture",
  photograph: "Photographie",
  graphic_novel: "Bande dessinée",
  manga: "Manga",
  video_game: "Jeu vidéo",
  software: "Logiciel",
  website: "Site web",
  other: "Autre",
};

// Form filter options
const FORM_OPTIONS = [
  { value: "", label: "Toutes les formes" },
  { value: "novel", label: "Roman" },
  { value: "film", label: "Film" },
  { value: "poetry", label: "Poésie" },
  { value: "theater", label: "Théâtre" },
  { value: "essay", label: "Essai" },
  { value: "graphic_novel", label: "Bande dessinée" },
  { value: "manga", label: "Manga" },
  { value: "symphony", label: "Symphonie" },
  { value: "opera", label: "Opéra" },
];

export function WorksListPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [formFilter, setFormFilter] = useState("");
  const [page, setPage] = useState(1);

  const {
    data: works,
    isLoading,
    error,
  } = useWorksSearch({
    q: searchQuery || undefined,
    form: formFilter || undefined,
    page,
    limit: 20,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Œuvres</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Explorez les œuvres du catalogue (modèle IFLA LRM)
          </p>
        </div>
        <Link
          to="/catalog/works/new"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nouvelle œuvre
        </Link>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        {/* Search input */}
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Rechercher une œuvre..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pr-4 pl-10 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
          />
        </div>

        {/* Form filter */}
        <select
          value={formFilter}
          onChange={(e) => {
            setFormFilter(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          {FORM_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          Erreur lors du chargement des œuvres.
        </div>
      ) : works && works.length > 0 ? (
        <div className="space-y-4">
          {works.map((work: WorkSummary) => (
            <Link
              key={work.id}
              to={`/catalog/works/${work.id}`}
              className="block rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-blue-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-700"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {work.preferredTitle}
                  </h2>
                  {work.creatorsDisplay && (
                    <p className="mt-1 text-gray-600 dark:text-gray-400">{work.creatorsDisplay}</p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {work.form && (
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {WORK_FORM_LABELS[work.form] || work.form}
                      </span>
                    )}
                    {work.dateOfWorkYear && (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                        {work.dateOfWorkYear}
                      </span>
                    )}
                    {work.originalLanguage && (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                        {work.originalLanguage.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {work.expressionCount}
                    </div>
                    <div className="text-xs">expr.</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {work.manifestationCount}
                    </div>
                    <div className="text-xs">manif.</div>
                  </div>
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          ))}

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
            <button
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
              }}
              disabled={page === 1}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Précédent
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">Page {page}</span>
            <button
              onClick={() => {
                setPage((p) => p + 1);
              }}
              disabled={works.length < 20}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Suivant
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-12 text-center dark:border-gray-700 dark:bg-gray-800/50">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-white">
            Aucune œuvre trouvée
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {searchQuery || formFilter
              ? "Essayez de modifier vos critères de recherche."
              : "Commencez par enrichir vos notices avec les œuvres BnF."}
          </p>
        </div>
      )}
    </div>
  );
}
